/**
 * Signal Framework v2 — domain types and the canonical occurrence vocabulary.
 *
 * Canon authority: docs/1-canon/23-signal-framework.md v2 §6.1–6.2.
 * PRD: docs/9-exploration/SIGNAL-RESPONSE-V2-PRD.md (SIG-0).
 *
 * Two-layer change vocabulary:
 *   - occurrence     — what happened. A chip from SIGNAL_OCCURRENCES or free
 *                      text. Deliberately NOT an enum: it exists for capture
 *                      friction, and it is valence-symmetric (good news is
 *                      also a signal).
 *   - routing class  — what it touches. A small closed enum that determines
 *                      the ceremony. Chips carry a DEFAULT routing class,
 *                      always overridable at reading time.
 *
 * No condition letter (A/B/C/D) appears anywhere in these types as a stored
 * or selectable state — exposure is derived from linkage (canon 23 §6.3).
 */

import type { Database } from './database.generated';

// ============================================================================
// Row types (generated contract)
// ============================================================================

export type SignalEventRow = Database['public']['Tables']['signal_events']['Row'];
export type SignalEventResponseRow =
  Database['public']['Tables']['signal_event_responses']['Row'];

/** Derived read-model rows (migration 673) — computed, never stored. */
export type SignalEventUnansweredRow =
  Database['public']['Views']['signal_events_unanswered']['Row'];
export type DecisionSignalExposureRow =
  Database['public']['Views']['decision_signal_exposure']['Row'];

// ============================================================================
// Closed enums (mirror the DB CHECK constraints in migration 672)
// ============================================================================

export const SIGNAL_ROUTING_CLASSES = [
  'feasibility_model',
  'value_model',
  'assumption_base',
  'mandate_frame',
  'record_error',
] as const;
export type SignalRoutingClass = (typeof SIGNAL_ROUTING_CLASSES)[number];

/** Honest present states only — "answered" is derived from linkage, never stored. */
export const SIGNAL_EVENT_STATUSES = ['monitoring', 'confirmed', 'dismissed'] as const;
export type SignalEventStatus = (typeof SIGNAL_EVENT_STATUSES)[number];

export const SIGNAL_SOURCES = ['human', 'machine'] as const;
export type SignalSource = (typeof SIGNAL_SOURCES)[number];

/**
 * The three honest present-tense responses (canon 23 §4.1) plus 'dismiss',
 * which is only ever written by dismiss_signal_event with a rationale.
 * No failure mode is selectable — by anyone.
 */
export const SIGNAL_RESPONSE_KINDS = ['change', 'hold', 'monitor', 'dismiss'] as const;
export type SignalResponseKind = (typeof SIGNAL_RESPONSE_KINDS)[number];

/** Derived exposure states (canon 23 §6.3) — computed, never stored. */
export const SIGNAL_EXPOSURE_STATES = [
  'on_signal',
  'change_unsignaled',
  'change_ahead_of_signal',
  'signal_unanswered',
] as const;
export type SignalExposureState = (typeof SIGNAL_EXPOSURE_STATES)[number];

// ============================================================================
// Pre-registered expectations (canon 23 §6.1 — the pre-registration requirement)
// ============================================================================

/**
 * "We are changing because X; if X is real, we expect Y; we will know by Z."
 * Lands in decisions.reconsideration_conditions + review_by_date (Doc 27 §6).
 */
export interface SignalExpectation {
  expectation: string;
  /** ISO date (YYYY-MM-DD). Feeds the Decision's review_by_date and the Docket. */
  check_by?: string;
}

// ============================================================================
// The canonical occurrence vocabulary (canon 23 §6.2)
// ============================================================================

export interface SignalOccurrence {
  /** Stable chip id — stored in signal_events.occurrence. */
  id: string;
  /** Capture-UI label, verbatim from the canon vocabulary. */
  label: string;
  /**
   * Default routing class, overridable at reading time. `null` means the
   * occurrence genuinely requires a reading before it can route (e.g. "risk
   * changes" is value_model when appetite shifts, feasibility_model when a
   * risk realizes).
   */
  defaultRoutingClass: SignalRoutingClass | null;
  source: SignalSource;
}

/**
 * Source: the investor narrative, "And decisions must change" (2026-08),
 * plus the product additions the canon names. Chips are the capture layer —
 * free text is always allowed alongside them.
 */
export const SIGNAL_OCCURRENCES: readonly SignalOccurrence[] = [
  // ── Human-captured (valence-symmetric by design) ──────────────────────────
  { id: 'customer_changes_mind', label: 'A customer changes their mind', defaultRoutingClass: 'value_model', source: 'human' },
  { id: 'harder_than_expected', label: 'Something proves much harder than expected', defaultRoutingClass: 'feasibility_model', source: 'human' },
  { id: 'constraint_disappears', label: 'A constraint disappears', defaultRoutingClass: 'feasibility_model', source: 'human' },
  { id: 'new_technology_appears', label: 'A new technology appears', defaultRoutingClass: 'value_model', source: 'human' },
  { id: 'competitor_moves', label: 'A competitor does something unexpected', defaultRoutingClass: 'value_model', source: 'human' },
  { id: 'economics_change', label: 'The economics change', defaultRoutingClass: 'value_model', source: 'human' },
  { id: 'resources_change', label: 'Your resources change', defaultRoutingClass: 'feasibility_model', source: 'human' },
  { id: 'leadership_changes', label: 'Leadership changes', defaultRoutingClass: 'mandate_frame', source: 'human' },
  { id: 'deadline_moves', label: 'A deadline moves', defaultRoutingClass: 'feasibility_model', source: 'human' },
  { id: 'dependency_fails', label: 'A dependency fails', defaultRoutingClass: 'feasibility_model', source: 'human' },
  { id: 'evidence_changes', label: 'The evidence changes', defaultRoutingClass: 'assumption_base', source: 'human' },
  { id: 'assumption_proves_wrong', label: 'An assumption proves wrong', defaultRoutingClass: 'assumption_base', source: 'human' },
  { id: 'assumption_proves_right', label: 'An assumption proves right', defaultRoutingClass: 'assumption_base', source: 'human' },
  { id: 'desired_outcome_changes', label: 'The desired outcome changes', defaultRoutingClass: 'value_model', source: 'human' },
  { id: 'time_horizon_changes', label: 'The time horizon changes', defaultRoutingClass: 'value_model', source: 'human' },
  // Requires a reading: appetite shift → value_model; risk realizing → feasibility_model.
  { id: 'risk_changes', label: 'Risk changes', defaultRoutingClass: null, source: 'human' },
  { id: 'unexpected_opportunity', label: 'An unexpected opportunity appears', defaultRoutingClass: 'value_model', source: 'human' },
  { id: 'works_better_than_expected', label: 'Something works better than expected', defaultRoutingClass: 'feasibility_model', source: 'human' },
  { id: 'fails_despite_doing_right', label: 'Something fails despite doing everything "right"', defaultRoutingClass: 'assumption_base', source: 'human' },
  { id: 'organization_changes', label: 'The organization itself changes', defaultRoutingClass: 'mandate_frame', source: 'human' },
  { id: 'record_error_discovered', label: 'An error in the record is discovered', defaultRoutingClass: 'record_error', source: 'human' },

  // ── Machine-generated (603 ids match that migration's signal_type values) ─
  { id: 'above_line_item_exited', label: 'A ranked item exited above the line', defaultRoutingClass: 'feasibility_model', source: 'machine' },
  { id: 'new_item_classified', label: 'A strategic item appeared outside the ranking', defaultRoutingClass: 'value_model', source: 'machine' },
  // Doc 102 §9.4: raised only when the value model itself is invalidated —
  // the one machine occurrence that arrives pre-routed (bridge in migration 673).
  { id: 'planning_reprioritization_recommended', label: 'A planning constraint recommends reprioritization', defaultRoutingClass: 'value_model', source: 'machine' },
  // Written by trip_commitment_term (migrations 680/682) when a kill criterion,
  // review trigger or expiry trips. Which model it calls into question depends
  // on the term, so it needs a reading before it can route.
  { id: 'commitment_term_tripped', label: 'A commitment term tripped', defaultRoutingClass: null, source: 'machine' },
] as const;

export const HUMAN_SIGNAL_OCCURRENCES: readonly SignalOccurrence[] =
  SIGNAL_OCCURRENCES.filter((o) => o.source === 'human');

export function getSignalOccurrence(id: string): SignalOccurrence | undefined {
  return SIGNAL_OCCURRENCES.find((o) => o.id === id);
}

/**
 * Default routing class for an occurrence, or null when the occurrence is
 * free text or genuinely requires a reading first.
 */
export function defaultRoutingClassFor(occurrence: string): SignalRoutingClass | null {
  return getSignalOccurrence(occurrence)?.defaultRoutingClass ?? null;
}
